import React, { useState, useRef, useEffect } from 'react';
import {
  Share2,
  Facebook,
  MessageCircle,
  Twitter,
  Linkedin,
  Mail,
  Link as LinkIcon,
  Check,
  X,
} from 'lucide-react';

// Build the canonical share URL for a story (deep-linked back to the impact-stories page).
const buildShareUrl = (storyId) => {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/impact-stories?story=${encodeURIComponent(storyId)}`;
};

const buildShareText = (story) => {
  const title = story?.title || 'Impact Story';
  return `${title} — Kioo Radio 98.1FM`;
};

/**
 * Reusable share button cluster for an Impact Story.
 *
 * Props:
 *   story    - the story object (must have id and title)
 *   variant  - 'card' | 'modal'   (visual size)
 *   compact  - if true on cards, shows only a single Share icon-button that opens a popover
 */
export default function StoryShareButtons({ story, variant = 'card', compact = true }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  const url = buildShareUrl(story.id);
  const text = buildShareText(story);
  const encUrl = encodeURIComponent(url);
  const encText = encodeURIComponent(text);

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const targets = [
    {
      key: 'facebook',
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`,
      Icon: Facebook,
      color: 'bg-[#1877F2] hover:bg-[#0f6ae0] text-white',
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      href: `https://wa.me/?text=${encText}%20${encUrl}`,
      Icon: MessageCircle,
      color: 'bg-[#25D366] hover:bg-[#1ebe5d] text-white',
    },
    {
      key: 'twitter',
      label: 'X (Twitter)',
      href: `https://twitter.com/intent/tweet?text=${encText}&url=${encUrl}`,
      Icon: Twitter,
      color: 'bg-black hover:bg-gray-800 text-white',
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
      Icon: Linkedin,
      color: 'bg-[#0A66C2] hover:bg-[#085196] text-white',
    },
    {
      key: 'email',
      label: 'Email',
      href: `mailto:?subject=${encText}&body=${encText}%0A%0A${encUrl}`,
      Icon: Mail,
      color: 'bg-gray-700 hover:bg-gray-800 text-white',
    },
  ];

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback: select via prompt
      window.prompt('Copy this link', url);
    }
  };

  const handleNativeShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.share({ title: story.title, text, url });
    } catch {
      /* user cancelled */
    }
  };

  // Close popover on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onDown = (ev) => {
      if (
        popRef.current &&
        !popRef.current.contains(ev.target) &&
        btnRef.current &&
        !btnRef.current.contains(ev.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const stop = (e) => e.stopPropagation();

  // ---------- Modal variant: full inline row ----------
  if (variant === 'modal') {
    return (
      <div
        data-testid={`story-share-modal-${story.id}`}
        className="flex flex-wrap items-center gap-2"
        onClick={stop}
      >
        <span className="text-sm font-semibold text-gray-700 mr-1 inline-flex items-center gap-1.5">
          <Share2 className="w-4 h-4" />
          Share:
        </span>
        {targets.map(({ key, label, href, Icon, color }) => (
          <a
            key={key}
            href={href}
            target={key === 'email' ? '_self' : '_blank'}
            rel="noopener noreferrer"
            aria-label={`Share on ${label}`}
            data-testid={`story-share-${key}-${story.id}`}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition ${color}`}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{label}</span>
          </a>
        ))}
        <button
          type="button"
          onClick={handleCopy}
          aria-label="Copy link"
          data-testid={`story-share-copy-${story.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition"
        >
          {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <LinkIcon className="w-4 h-4" />}
          <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy link'}</span>
        </button>
        {canNativeShare && (
          <button
            type="button"
            onClick={handleNativeShare}
            aria-label="More share options"
            data-testid={`story-share-native-${story.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-kioo-primary text-white hover:opacity-90 transition"
          >
            <Share2 className="w-4 h-4" />
            <span className="hidden sm:inline">More</span>
          </button>
        )}
      </div>
    );
  }

  // ---------- Card variant: compact icon-button + popover ----------
  return (
    <div className="relative inline-block" onClick={stop}>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          stop(e);
          if (canNativeShare && compact) {
            handleNativeShare(e);
            return;
          }
          setOpen((o) => !o);
        }}
        aria-label="Share this story"
        data-testid={`story-share-btn-${story.id}`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-800 transition"
      >
        <Share2 className="w-4 h-4" />
        Share
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid={`story-share-popover-${story.id}`}
          className="absolute right-0 bottom-full mb-2 z-30 w-60 bg-white rounded-xl shadow-xl border border-gray-200 p-2"
        >
          <div className="flex items-center justify-between px-2 py-1 mb-1">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Share story
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close share menu"
              className="w-6 h-6 rounded-full text-gray-500 hover:bg-gray-100 flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-col">
            {targets.map(({ key, label, href, Icon }) => (
              <a
                key={key}
                href={href}
                target={key === 'email' ? '_self' : '_blank'}
                rel="noopener noreferrer"
                data-testid={`story-share-${key}-${story.id}`}
                className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-gray-800 hover:bg-gray-50"
                onClick={() => setOpen(false)}
              >
                <span className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-100">
                  <Icon className="w-4 h-4 text-gray-700" />
                </span>
                {label}
              </a>
            ))}
            <button
              type="button"
              onClick={handleCopy}
              data-testid={`story-share-copy-${story.id}`}
              className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-gray-800 hover:bg-gray-50 text-left"
            >
              <span className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-100">
                {copied ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <LinkIcon className="w-4 h-4 text-gray-700" />
                )}
              </span>
              {copied ? 'Link copied!' : 'Copy link'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
